import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin, Linkedin, Instagram, Facebook, ArrowUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedPath } from "@/lib/routes";
import onepractixLogo from "@/assets/onepractix-logo-new.svg";

const socialLinks = [
{ icon: Linkedin, href: "https://linkedin.com/company/onepractix", label: "LinkedIn" },
{ icon: Instagram, href: "https://instagram.com/onepractix", label: "Instagram" },
{ icon: Facebook, href: "https://www.facebook.com/onepractix", label: "Facebook" }];


export function Footer() {
  const { t, language } = useLanguage();
  const location = useLocation();

  // Handle click for same-page navigation
  const handleNavClick = (path: string) => {
    if (location.pathname === path && !path.includes('#')) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const homePath = getLocalizedPath("home", language);
  const servicesPath = getLocalizedPath("services", language);

  const navLinks = [
    { name: t("nav.home"), path: homePath },
    { name: t("nav.services"), path: servicesPath },
    { name: t("nav.about"), path: getLocalizedPath("about", language) },
    { name: t("nav.caseStudies"), path: getLocalizedPath("caseStudies", language) },
    { name: language === "de" ? "Für wen" : "Who it is for", path: getLocalizedPath("whoItIsFor", language) },
    { name: t("nav.insights"), path: getLocalizedPath("blog", language) },
    { name: t("nav.contact"), path: getLocalizedPath("contact", language) },
  ];

  const services = [
    { name: t("footer.service1"), path: `${servicesPath}#website` },
    { name: t("footer.service2"), path: `${servicesPath}#terminbuchung` },
    { name: t("footer.service3"), path: `${servicesPath}#sichtbarkeit` },
    { name: t("footer.packages") || "Packages", path: `${servicesPath}#pakete` },
  ];

  const linkClass = "group inline-flex w-fit py-1.5 md:py-0.5 text-[0.85rem] md:text-[0.8rem] text-foreground/60 hover:text-foreground hover:underline underline-offset-2 transition-colors";
  const headingClass = "mb-3 text-[0.8rem] font-semibold tracking-normal text-foreground/90";

  return (
    <footer className="op-silver relative" style={{ containIntrinsicBlockSize: '400px', contentVisibility: 'auto' }}>
      <div className="container">
        <div className="pt-12 pb-8 md:pt-14">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-5">
              {/* Negative margin compensates for SVG internal whitespace; calibrated to logo render size (h-11) */}
              <Link to={homePath} className="inline-flex items-center gap-2 group -ml-[24px]">
                <img
                  src={onepractixLogo}
                  alt=""
                  role="presentation"
                  width={66}
                  height={44}
                  className="h-11 object-contain" />

                <span className="font-brand text-lg font-bold tracking-tight">
                  <span className="text-primary group-hover:text-primary/80 transition-colors">One</span>
                  <span className="text-foreground group-hover:text-foreground/80 transition-colors">Practix</span>
                </span>
              </Link>
              <p className="mt-4 text-[0.85rem] text-foreground/60 leading-relaxed max-w-sm text-left">
                {t("footer.description")}
              </p>
              <div className="mt-6 flex gap-2">
                {socialLinks.map((social) =>
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-magnetic="4"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.05] text-foreground/70 hover:bg-primary hover:text-primary-foreground"
                  aria-label={social.label}>

                    <social.icon className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>

            {/* Services */}
            <div className="lg:col-span-2">
              <h3 className={headingClass} style={{ fontFamily: "inherit" }}>{t("footer.services")}</h3>
              <nav aria-label="Footer services navigation" className="flex flex-col gap-0.5 md:gap-1.5">
                {services.map((service) =>
                <Link
                  key={service.path}
                  to={service.path}
                  className={linkClass}>

                    <span>{service.name}</span>
                  </Link>
                )}
              </nav>
            </div>

            {/* Navigation */}
            <div className="lg:col-span-2">
              <h3 className={headingClass} style={{ fontFamily: "inherit" }}>{t("footer.explore")}</h3>
              <nav aria-label="Footer navigation" className="flex flex-col gap-0.5 md:gap-1.5">
                {navLinks.map((link) =>
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={linkClass}>

                    <span>{link.name}</span>
                  </Link>
                )}
              </nav>
            </div>

            {/* Contact */}
            <div className="lg:col-span-3">
              <h3 className={headingClass} style={{ fontFamily: "inherit" }}>{t("footer.contact")}</h3>
              <div className="flex flex-col gap-4">
                <a
                  href="mailto:info@onepractix.com"
                  className="group flex items-center gap-3 text-[0.8rem] text-foreground/60 hover:text-foreground transition-colors">

                  <span className="grid h-8 w-8 place-items-center rounded-full bg-black/[0.05] text-foreground/70"><Mail size={14} /></span>
                  <span>info@onepractix.com</span>
                </a>
                <span className="flex items-start gap-3 text-[0.8rem] text-foreground/60">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/[0.05] text-foreground/70"><MapPin size={14} /></span>
                  <span className="pt-1.5">Tolstého 5<br />811 06 Bratislava, {language === "de" ? "Slowakei" : "Slovakia"}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-black/[0.1] pt-5 md:flex-row">
            <p className="text-[0.8rem] text-foreground/60">
              © 2026 OnePractix. {t("footer.rights")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
              <Link to={getLocalizedPath("imprint", language)} className={linkClass}>
                <span>{language === "de" ? "Impressum" : "Imprint"}</span>
              </Link>
              <Link to={getLocalizedPath("privacy", language)} className={linkClass}>
                <span>{t("footer.privacy")}</span>
              </Link>
              <Link to={getLocalizedPath("terms", language)} className={linkClass}>
                <span>{t("footer.terms")}</span>
              </Link>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
                aria-label={language === "de" ? "Nach oben" : "Back to top"}
                className="hidden md:grid h-8 w-8 place-items-center rounded-full bg-black/[0.05] text-foreground/70 transition-all duration-500 hover:-translate-y-0.5 hover:bg-primary hover:text-primary-foreground"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>);

}
